import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Steps, Form, Input, Button, Select, DatePicker, TimePicker, InputNumber, Row, Col, Radio, message } from 'antd';
import moment from 'moment';
import api from "../api/axios";
import { Country, State, City } from 'country-state-city';
import CustomNavbar from '../components/Navbar';



// Import JSON data files
import religionData from '../data/religion.json';
import denominationData from '../data/denomination.json';
import casteData from '../data/caste.json';
import subcasteData from '../data/subcaste.json';
import ISO6391 from 'iso-639-1';

const { Step } = Steps;
const { TextArea } = Input;
const { Option } = Select;

const AddProfile = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [navbarReady, setNavbarReady] = useState(false);
    const [latLon, setLatLon] = useState({ latitude: '', longitude: '' });
    const [selectedCountry, setSelectedCountry] = useState({ name: '', isoCode: '' });
    const [selectedState, setSelectedState] = useState({ name: '', isoCode: '' });
    const [form] = Form.useForm();

    const disabledDate = (current) => {
        // Can not select days after today or dates that make the user younger than 18 years
        return current && (current > moment().endOf('day') || current > moment().subtract(18, 'years').endOf('day'));
    };

    // Function to convert feet and inches to centimeters correctly
    const convertToCm = (feet, inches) => {
        const totalInches = (feet * 12) + inches; // Convert feet to inches and add extra inches
        return Math.round(totalInches * 2.54); // Convert to centimeters (1 inch = 2.54 cm)
    };

    // Generate height options in feet, inches, and centimeters
    const heights = [];
    for (let feet = 4; feet <= 7; feet++) { // Loop for feet range (4ft to 7ft)
        for (let inches = 0; inches < 12; inches++) { // Loop for inches (0 to 11)
            if (feet === 7 && inches > 0) break; // Stop at exactly 7ft (no extra inches)
            const heightInCm = convertToCm(feet, inches); // Convert feet & inches to cm
            heights.push({
                label: `${feet}ft ${inches}in (${heightInCm} cm)`, // Label for display
                value: `${feet}.${inches.toString().padStart(2, '0')}` // Numeric value (e.g., 5.06 for 5ft 6in)
            });
        }
    }

    const fetchLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    const { latitude, longitude } = position.coords;
                    setLatLon({ latitude, longitude });
                    form.setFieldsValue({ latitude, longitude });
                },
                error => {
                    console.error("Error fetching location: ", error);
                }
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
        }
    };

    const handleBasicInfoSubmit = async (values) => {
        setLoading(true);
        const dob = form.getFieldValue('dob');
        const tob = form.getFieldValue('timeOfBirth');
        const dateOfBirth = dob ? dob.format("YYYY-MM-DD") : null;
        const timeOfBirth = tob ? tob.format("HH:mm") : null;

        const payload = {
            first_name: values.firstName,
            middle_name: values.middleName,
            last_name: values.lastName,
            date_of_birth: dateOfBirth,
            time_of_birth: timeOfBirth,
            place_of_birth: values.placeOfBirth,
            gender: values.gender,
            marital_status: values.maritalStatus,
            current_location: values.currentLocation,
            profile_created_for: values.profileCreatedFor,
        };

        try {
            const response = await api.post('/profiles/add', payload);

            if (response.status === 201) {
                const profileData = response.data?.data;
                if (profileData && profileData.profile_id) {
                    // Extract profile_id from the API response
                    const profileId = profileData.profile_id;

                    // Save profile_id in session storage for later steps
                    sessionStorage.setItem("submitted_profile_id", profileId);

                    // Update the form data and proceed to the next step
                    setFormData({ ...formData, ...values, profile_id: profileId });
                    setCurrentStep(currentStep + 1);
                    message.success("Basic information added successfully!");
                } else {
                    // Handle missing profile_id in the API response
                    message.error("Profile ID is missing in the server response. Please try again.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Other errors
                message.error("An error occurred while adding the profile. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEducationInfoSubmit = async (values) => {
        setLoading(true);

        const payload = {
            profile_id: sessionStorage.getItem("submitted_profile_id"),
            education_category: values.educationCategory,
            highest_education: values.highestEducation,
            education_detail: values.educationDetail,
            occupation_detail: values.occupationDetail,
            occupation: values.occupation,
            annual_income_range: values.annualIncomeRange,
            employed_in: values.employedIn,
        };

        try {
            const response = await api.post('/education-occupation/add', payload);

            if (response.status === 201) {
                const educationOccupationData = response.data?.data;
                if (educationOccupationData) {
                    setFormData({ ...formData, ...values });
                    setCurrentStep(currentStep + 1);
                    message.success("Education & Occupation added successfully!");
                } else {
                    message.error("An unexpected error occurred while adding Education & Occupation.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors from the backend
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Handle other errors (server or network issues)
                message.error("An error occurred while adding the Education & Occupation. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSocioReligiousInfo = async (values) => {
        setLoading(true);

        const payload = {
            profile_id: sessionStorage.getItem("submitted_profile_id"),
            religion: values.religion,
            denomination: values.denomination,
            caste: values.caste,
            subcaste: values.subCaste,
            division: values.division,
            mother_tongue: values.motherTongue,
        };

        try {
            const response = await api.post('/socio-religious-background/add', payload);

            if (response.status === 201) {
                const socioReligiousBackgroundData = response.data?.data;
                if (socioReligiousBackgroundData) {
                    setFormData({ ...formData, ...values });
                    setCurrentStep(currentStep + 1);
                    message.success("Socio-religious Background added successfully!");
                } else {
                    message.error("An unexpected error occurred while adding Socio-religious Background.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors from the backend
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Handle other errors (server or network issues)
                message.error("An error occurred while adding the Socio-religious Background. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePhysicalStatusInfo = async (values) => {
        setLoading(true);

        const payload = {
            profile_id: sessionStorage.getItem("submitted_profile_id"),
            height: values.height,
            weight: values.weight,
            body_type: values.bodyType,
            physical_status: values.physicalStatus,
            complexion: values.complexion,
            blood_group: values.bloodGroup,
            diet: values.diet,
            smoke: values.smoke,
            drink: values.drink,
        };

        try {
            const response = await api.post('/physical-lifestyle/add', payload);

            if (response.status === 201) {
                const physicalLifestyleData = response.data?.data;
                if (physicalLifestyleData) {
                    setFormData({ ...formData, ...values });
                    setCurrentStep(currentStep + 1);
                    message.success("Physical lifestyle details added successfully!");
                } else {
                    message.error("An unexpected error occurred while adding Physical lifestyle details.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors from the backend
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Handle other errors (server or network issues)
                message.error("An error occurred while adding the Physical lifestyle details. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFamilyInfo = async (values) => {
        setLoading(true);

        const payload = {
            profile_id: sessionStorage.getItem("submitted_profile_id"),
            family_values: values.familyValues,
            family_type: values.familyType,
            family_origin: values.familyOrigin,
            father_name: values.fathersName,
            father_alive: values.fathersAlive === 'Yes' ? true : false,
            father_occupation: values.fathersOccupation,
            mother_name: values.mothersName,
            mother_alive: values.mothersAlive === 'Yes' ? true : false,
            mother_occupation: values.mothersOccupation,
            no_of_brothers: values.numberOfBrothers,
            no_of_brothers_married: values.numberOfBrothersMarried,
            no_of_sisters: values.numberOfSisters,
            no_of_sisters_married: values.numberOfSistersMarried,
        };

        try {
            const response = await api.post('/family-information/add', payload);

            if (response.status === 201) {
                const familyInformationData = response.data?.data;
                if (familyInformationData) {
                    setFormData({ ...formData, ...values });
                    setCurrentStep(currentStep + 1);
                    message.success("Family information added successfully!");
                } else {
                    message.error("An unexpected error occurred while adding Family information.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors from the backend
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Handle other errors (server or network issues)
                message.error("An error occurred while adding the Family information. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleContactInfo = async (values) => {
        setLoading(true);

        const payload = {
            profile_id: sessionStorage.getItem("submitted_profile_id"),
            mobile_number: values.mobileNumber,
            alternative_mobile_number: values.alternativeMobileNumber,
            whatsapp_number: values.whatsappNumber,
            email_address: values.emailAddress,
            door_no: values.doorNo,
            address_1: values.address1,
            address_2: values.address2,
            landmark: values.landmark,
            country: values.country,
            state: values.state,
            city: values.city,
            district: values.district,
            postal_code: values.postalCode,
            latitude: values.latitude || latLon.latitude,
            longitude: values.longitude || latLon.longitude,
        };

        try {
            const response = await api.post('/contact-information/add', payload);

            if (response.status === 201) {
                const contactInformationData = response.data?.data;
                if (contactInformationData) {
                    setFormData({});
                    sessionStorage.removeItem("submitted_profile_id");
                    message.success("Contact information added successfully!");
                    navigate('/dashboard');
                } else {
                    message.error("An unexpected error occurred while adding Contact information.");
                }
            }
        } catch (error) {
            // Handle errors
            if (error.response && error.response.status === 422) {
                // Validation errors from the backend
                const errors = error.response.data.message;
                Object.keys(errors).forEach((field) => {
                    message.error(`${field}: ${errors[field].join(", ")}`);
                });
            } else {
                // Handle other errors (server or network issues)
                message.error("An error occurred while adding the Contact information. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        {
            title: 'Basic Information',
            content: (
                <Form form={form} layout="vertical" onFinish={handleBasicInfoSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Please enter your first name' }]}>
                                <Input placeholder="First Name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="middleName" label="Middle Name">
                                <Input placeholder="Middle Name" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Please enter your last name' }]}>
                                <Input placeholder="Last Name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="dob" label="Date of Birth" rules={[{ required: true, message: 'Please select your date of birth' }]}>
                                <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="timeOfBirth"
                                label="Time of Birth"
                            >
                                <TimePicker
                                    placeholder="Select Time of Birth"
                                    format="HH:mm" // Specify the format (e.g., 24-hour format)
                                    style={{ width: '100%' }} // Ensure it takes full width
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item name="placeOfBirth" label="Place of Birth">
                                <Input placeholder="Place of Birth" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Please select your gender' }]}>
                                <Radio.Group>
                                    <Radio value="Male">Male</Radio>
                                    <Radio value="Female">Female</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="maritalStatus" label="Marital Status" rules={[{ required: true, message: 'Please select your marital status' }]}>
                                <Select placeholder="Select Marital Status">
                                    <Option value="Single">Single</Option>
                                    <Option value="Divorced">Divorced</Option>
                                    <Option value="Widowed">Widowed</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="currentLocation" label="Current Location">
                                <Input placeholder="Current Location" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="profileCreatedFor" label="Profile Created For" rules={[{ required: true, message: 'Please select who the profile is created for' }]}>
                                <Select placeholder="Select">
                                    <Option value="Self">Self</Option>
                                    <Option value="Son">Son</Option>
                                    <Option value="Daughter">Daughter</Option>
                                    <Option value="Sibling">Sibling</Option>
                                    <Option value="Friend">Friend</Option>
                                    <Option value="Relative">Relative</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Next'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: 'Education and Occupation',
            content: (
                <Form form={form} layout="vertical" onFinish={handleEducationInfoSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="educationCategory" label="Education Category" rules={[{ required: true, message: 'Please select your education category' }]}>
                                <Select placeholder="Select Education Category">
                                    <Option value="Engineering">Engineering</Option>
                                    <Option value="Medicine">Medicine</Option>
                                    <Option value="Arts">Arts</Option>
                                    <Option value="Science">Science</Option>
                                    <Option value="Commerce">Commerce</Option>
                                    <Option value="Law">Law</Option>
                                    <Option value="Architecture">Architecture</Option>
                                    <Option value="Agriculture">Agriculture</Option>
                                    <Option value="Education">Education</Option>
                                    <Option value="Management">Management</Option>
                                    <Option value="Computer Science">Computer Science</Option>
                                    <Option value="Information Technology">Information Technology</Option>
                                    <Option value="Design">Design</Option>
                                    <Option value="Media">Media</Option>
                                    <Option value="Hospitality">Hospitality</Option>
                                    <Option value="Tourism">Tourism</Option>
                                    <Option value="Aviation">Aviation</Option>
                                    <Option value="Maritime">Maritime</Option>
                                    <Option value="Defence">Defence</Option>
                                    <Option value="Civil Services">Civil Services</Option>
                                    <Option value="Social Work">Social Work</Option>
                                    <Option value="Environmental Science">Environmental Science</Option>
                                    <Option value="Biotechnology">Biotechnology</Option>
                                    <Option value="Pharmacy">Pharmacy</Option>
                                    <Option value="Nursing">Nursing</Option>
                                    <Option value="Physiotherapy">Physiotherapy</Option>
                                    <Option value="Alternative Medicine">Alternative Medicine</Option>
                                    <Option value="Veterinary">Veterinary</Option>
                                    <Option value="Chartered Accountancy">Chartered Accountancy</Option>
                                    <Option value="Company Secretary">Company Secretary</Option>
                                    <Option value="Cost Accountancy">Cost Accountancy</Option>
                                    <Option value="Actuarial Science">Actuarial Science</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="highestEducation" label="Highest Education" rules={[{ required: true, message: 'Please select your highest education' }]}>
                                <Select placeholder="Select Highest Education">
                                    <Option value="Below 10th">Below 10th</Option>
                                    <Option value="10th Pass / SSLC">10th Pass / SSLC</Option>
                                    <Option value="12th Pass / HSC">12th Pass / HSC</Option>
                                    <Option value="Diploma / Polytechnic">Diploma / Polytechnic</Option>
                                    <Option value="Graduate / Bachelor's">Graduate / Bachelor's</Option>
                                    <Option value="Postgraduate / Master's">Postgraduate / Master's</Option>
                                    <Option value="Doctorate / PhD">Doctorate / PhD</Option>
                                    <Option value="Professional Degree">Professional Degree</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="educationDetail" label="Education Detail">
                                <TextArea placeholder="Education Detail" rows={5} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="occupationDetail" label="Occupation Detail">
                                <TextArea placeholder="Occupation Detail" rows={5} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="occupation" label="Occupation" rules={[{ required: true, message: 'Please select your occupation' }]}>
                                <Select placeholder="Select Occupation">
                                    <Option value="Engineer">Engineer</Option>
                                    <Option value="Doctor">Doctor</Option>
                                    <Option value="Teacher">Teacher</Option>
                                    <Option value="Business">Business</Option>
                                    <Option value="Self-employed">Self-employed</Option>
                                    <Option value="Lawyer">Lawyer</Option>
                                    <Option value="Architect">Architect</Option>
                                    <Option value="Scientist">Scientist</Option>
                                    <Option value="Artist">Artist</Option>
                                    <Option value="Researcher">Researcher</Option>
                                    <Option value="Writer">Writer</Option>
                                    <Option value="CA/Accountant">CA/Accountant</Option>
                                    <Option value="IT Professional">IT Professional</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="annualIncomeRange"
                                label="Annual Income Range"
                                rules={[{ required: true, message: 'Please select your annual income range' }]}
                            >
                                <Select placeholder="Select Annual Income Range">
                                    <Option value="0-5 Lakhs">0-5 Lakhs</Option>
                                    <Option value="5-10 Lakhs">5-10 Lakhs</Option>
                                    <Option value="10-15 Lakhs">10-15 Lakhs</Option>
                                    <Option value="15-20 Lakhs">15-20 Lakhs</Option>
                                    <Option value="20-25 Lakhs">20-25 Lakhs</Option>
                                    <Option value="25-30 Lakhs">25-30 Lakhs</Option>
                                    <Option value="30-40 Lakhs">30-40 Lakhs</Option>
                                    <Option value="40-50 Lakhs">40-50 Lakhs</Option>
                                    <Option value="50 Lakhs - 1 Crore">50 Lakhs - 1 Crore</Option>
                                    <Option value="1-2 Crore">1-2 Crore</Option>
                                    <Option value="2-5 Crore">2-5 Crore</Option>
                                    <Option value="5-10 Crore">5-10 Crore</Option>
                                    <Option value="10-20 Crore">10-20 Crore</Option>
                                    <Option value="20-50 Crore">20-50 Crore</Option>
                                    <Option value="50-100 Crore">50-100 Crore</Option>
                                    <Option value="100+ Crore">100+ Crore</Option>
                                    <Option value="No Income">No Income</Option>
                                    <Option value="Prefer not to say">Prefer not to say</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="employedIn" label="Employed In" rules={[{ required: true, message: 'Please select your employment type' }]}>
                                <Select placeholder="Select Employment Type">
                                    <Option value="Government">Government</Option>
                                    <Option value="Private">Private</Option>
                                    <Option value="Business">Business</Option>
                                    <Option value="Self-employed">Self-employed</Option>
                                    <Option value="Defence/Civil Services">Defence/Civil Services</Option>
                                    <Option value="NGO/Social Sector">NGO/Social Sector</Option>
                                    <Option value="Student">Student</Option>
                                    <Option value="Homemaker">Homemaker</Option>
                                    <Option value="Retired">Retired</Option>
                                    <Option value="Not working">Not working</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Next'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: 'Socio Religious Background',
            content: (
                <Form form={form} layout="vertical" onFinish={handleSocioReligiousInfo}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="religion" label="Religion" rules={[{ required: true, message: 'Please select your religion' }]}>
                                <Select placeholder="Select Religion">
                                    {religionData.religions.map(religion => (
                                        <Option key={religion.id} value={religion.name}>{religion.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="denomination" label="Denomination">
                                <Select placeholder="Select Denomination">
                                    {denominationData.denominations.map(denomination => (
                                        <Option key={denomination.id} value={denomination.name}>{denomination.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="caste" label="Caste">
                                <Select placeholder="Select Caste">
                                    {casteData.castes.map(caste => (
                                        <Option key={caste.id} value={caste.name}>{caste.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="subCaste" label="Sub Caste">
                                <Select placeholder="Select Sub Caste">
                                    {subcasteData.subcastes.map(subcaste => (
                                        <Option key={subcaste.id} value={subcaste.name}>{subcaste.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="motherTongue" label="Mother Tongue">
                                <Select placeholder="Select Mother Tongue" showSearch optionFilterProp="children">
                                    {ISO6391.getAllCodes().map(code => (
                                        <Option key={code} value={ISO6391.getName(code)}>
                                            {ISO6391.getName(code)}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Next'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: 'Physical Status and Lifestyle',
            content: (
                <Form form={form} layout="vertical" onFinish={handlePhysicalStatusInfo}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="height" label="Height" rules={[{ required: true, message: 'Please select your height' }]}>
                                <Select placeholder="Select Height">
                                    {heights.map(height => (
                                        <Option key={height.value} value={height.value}>{height.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="weight"
                                label="Weight"
                                rules={[
                                    { required: true, message: 'Please enter your weight' },
                                    { type: 'number', min: 30, max: 200, message: 'Weight must be between 30 and 200' }
                                ]}
                            >
                                <InputNumber placeholder="Weight" min={30} max={200} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="bodyType" label="Body Type">
                                <Select placeholder="Select Body Type">
                                    <Option value="Slim">Slim</Option>
                                    <Option value="Athletic">Athletic</Option>
                                    <Option value="Average">Average</Option>
                                    <Option value="Heavy">Heavy</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="physicalStatus" label="Physical Status">
                                <Select placeholder="Select Physical Status">
                                    <Option value="Normal">Normal</Option>
                                    <Option value="Physically Challenged">Physically Challenged</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="complexion" label="Complexion">
                                <Select placeholder="Select Complexion">
                                    <Option value="Fair">Fair</Option>
                                    <Option value="Medium">Medium</Option>
                                    <Option value="Dark">Dark</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="bloodGroup" label="Blood Group">
                                <Select placeholder="Select Blood Group">
                                    <Option value="A+">A+</Option>
                                    <Option value="A-">A-</Option>
                                    <Option value="B+">B+</Option>
                                    <Option value="B-">B-</Option>
                                    <Option value="AB+">AB+</Option>
                                    <Option value="AB-">AB-</Option>
                                    <Option value="O+">O+</Option>
                                    <Option value="O-">O-</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="diet" label="Diet">
                                <Select placeholder="Select Diet">
                                    <Option value="Veg">Veg</Option>
                                    <Option value="Non-Veg">Non-Veg</Option>
                                    <Option value="Eggetarian">Eggetarian</Option>
                                    <Option value="Vegan">Vegan</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="smoke" label="Smoking Habit">
                                <Radio.Group>
                                    <Radio value="Yes">Yes</Radio>
                                    <Radio value="No">No</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="drink" label="Alcohol consumption">
                                <Radio.Group>
                                    <Radio value="Yes">Yes</Radio>
                                    <Radio value="No">No</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Next'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: 'Family Information',
            content: (
                <Form form={form} layout="vertical" onFinish={handleFamilyInfo}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="familyValues"
                                label="Family Values"
                                rules={[{ required: true, message: 'Please select your family values' }]}
                            >
                                <Select placeholder="Select Family Values">
                                    <Option value="Traditional">Traditional</Option>
                                    <Option value="Moderate">Moderate</Option>
                                    <Option value="Liberal">Liberal</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="familyType"
                                label="Family Type"
                                rules={[{ required: true, message: 'Please select your family type' }]}
                            >
                                <Select placeholder="Select Family Type">
                                    <Option value="Joint">Joint</Option>
                                    <Option value="Nuclear">Nuclear</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="familyOrigin"
                                label="Family Origin"
                                rules={[{ required: true, message: 'Please enter your family origin' }]}
                            >
                                <Input placeholder="Family Origin" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="fathersName"
                                label="Father's Name"
                                rules={[{ required: true, message: 'Please enter your father\'s name' }]}
                            >
                                <Input placeholder="Father's Name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="fathersOccupation"
                                label="Father's Occupation"
                                rules={[{ required: true, message: 'Please enter your father\'s occupation' }]}
                            >
                                <Input placeholder="Father's Occupation" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="fathersAlive"
                                label="Is Father Alive?"
                                initialValue="Yes"
                                rules={[{ required: true, message: 'Please select if your father is alive' }]}
                            >
                                <Radio.Group>
                                    <Radio value="Yes">Yes</Radio>
                                    <Radio value="No">No</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="mothersName"
                                label="Mother's Name"
                                rules={[{ required: true, message: 'Please enter your mother\'s name' }]}
                            >
                                <Input placeholder="Mother's Name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="mothersOccupation"
                                label="Mother's Occupation"
                                rules={[{ required: true, message: 'Please enter your mother\'s occupation' }]}
                            >
                                <Input placeholder="Mother's Occupation" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="mothersAlive"
                                label="Is Mother Alive?"
                                initialValue="Yes"
                                rules={[{ required: true, message: 'Please select if your mother is alive' }]}
                            >
                                <Radio.Group>
                                    <Radio value="Yes">Yes</Radio>
                                    <Radio value="No">No</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="numberOfBrothers"
                                label="No. of Brothers"
                                rules={[{ required: true, message: 'Please enter the number of brothers' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="numberOfBrothersMarried"
                                label="No. of Brothers Married"
                                rules={[{ required: true, message: 'Please enter the number of brothers married' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="numberOfSisters"
                                label="No. of Sisters"
                                rules={[{ required: true, message: 'Please enter the number of sisters' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="numberOfSistersMarried"
                                label="No. of Sisters Married"
                                rules={[{ required: true, message: 'Please enter the number of sisters married' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Next'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
        {
            title: 'Contact Information',
            content: (
                <Form form={form} layout="vertical" onFinish={handleContactInfo}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="mobileNumber" label="Mobile Number" rules={[{ required: true, message: 'Please enter your mobile number' }]}>
                                <Input placeholder="Mobile Number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="alternativeMobileNumber" label="Alternative Mobile Number">
                                <Input placeholder="Alternative Mobile Number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="whatsappNumber" label="WhatsApp Number" rules={[{ required: true, message: 'Please enter your WhatsApp mobile number' }]}>
                                <Input placeholder="WhatsApp Number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="emailAddress" label="Email Address" rules={[{ required: true, message: 'Please enter your email address' }]}>
                                <Input placeholder="Email Address" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="doorNo" label="Door No" rules={[{ required: true, message: 'Please enter your door number' }]}>
                                <Input placeholder="Door No" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="address1" label="Address 1" rules={[{ required: true, message: 'Please enter your address' }]}>
                                <Input placeholder="Address 1" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="address2" label="Address 2">
                                <Input placeholder="Address 2" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="landmark" label="Landmark">
                                <Input placeholder="Landmark" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Please select your country' }]}>
                                <Select
                                    showSearch
                                    placeholder="Select Country"
                                    onChange={value => {
                                        if (value) {
                                            const countryObj = Country.getAllCountries().find(c => c.name === value);
                                            setSelectedCountry(countryObj ? { name: countryObj.name, isoCode: countryObj.isoCode } : { name: '', isoCode: '' });
                                            setSelectedState({ name: '', isoCode: '' });
                                            form.setFieldsValue({ state: undefined, city: undefined });
                                        } else {
                                            setSelectedCountry({ name: '', isoCode: '' });
                                            setSelectedState({ name: '', isoCode: '' });
                                        }
                                    }}
                                    optionFilterProp="children"
                                >
                                    {Country.getAllCountries().map(country => (
                                        <Select.Option key={country.isoCode} value={country.name}>
                                            {country.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="state" label="State" rules={[{ required: true, message: 'Please select your state' }]}>
                                <Select
                                    showSearch
                                    placeholder="Select State"
                                    onChange={value => {
                                        if (value) {
                                            const stateObj = selectedCountry.isoCode ? State.getStatesOfCountry(selectedCountry.isoCode).find(s => s.name === value) : null;
                                            setSelectedState(stateObj ? { name: stateObj.name, isoCode: stateObj.isoCode } : { name: '', isoCode: '' });
                                            form.setFieldsValue({ city: undefined });
                                        } else {
                                            setSelectedState({ name: '', isoCode: '' });
                                        }
                                    }}
                                    disabled={!selectedCountry.isoCode}
                                    optionFilterProp="children"
                                >
                                    {selectedCountry.isoCode &&
                                        State.getStatesOfCountry(selectedCountry.isoCode).map(state => (
                                            <Select.Option key={state.isoCode} value={state.name}>
                                                {state.name}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="city" label="City" rules={[{ required: true, message: 'Please select your city' }]}>
                                <Select
                                    showSearch
                                    placeholder="Select City"
                                    disabled={!selectedState.isoCode}
                                    optionFilterProp="children"
                                >
                                    {selectedCountry.isoCode && selectedState.isoCode &&
                                        City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(city => (
                                            <Select.Option key={city.name} value={city.name}>
                                                {city.name}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="district" label="District" rules={[{ required: true, message: 'Please enter your district' }]}>
                                <Input placeholder="District" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="postalCode" label="Postal Code" rules={[{ required: true, message: 'Please enter your postal code' }]}>
                                <Input placeholder="Postal Code" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="latitude" label="Latitude" rules={[{ required: true, message: 'Please enter the latitude' }]}>
                                <Input placeholder="Latitude" value={latLon.latitude} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="longitude" label="Longitude" rules={[{ required: true, message: 'Please enter the longitude' }]}>
                                <Input placeholder="Longitude" value={latLon.longitude} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} style={{ display: 'flex', alignItems: 'center' }}>
                            <Button type="primary" onClick={fetchLocation}>
                                Get Current Location
                            </Button>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                        >
                            {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Form.Item>
                </Form>
            ),
        },
    ];

    return (
        <div className='container-fluid' style={{ fontFamily: 'Calibri, sans-serif', padding: '50px' }}>
            <Helmet><title>Add Profile | Oottan</title></Helmet>
            <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />
            {navbarReady && (
                <div style={{ marginTop: '64px' }}>
                    <h2 className="mt-5 mb-4 display-5" style={{ fontSize: '1.5rem', textAlign: 'center' }}>Create New Profile</h2>
                    <Steps current={currentStep}>
                        {steps.map((step) => (
                            <Step key={step.title} title={step.title} />
                        ))}
                    </Steps>
                    <div style={{ marginTop: '24px' }}>{steps[currentStep].content}</div>
                </div>
            )}
        </div>
    );
};

export default AddProfile;